<template>
  <table class="w-full table-fixed">
    <thead>
      <tr class="border-b-2 border-gray-200">
        <th v-if="isSelectable" class="text-left w-2"></th>
        <th v-if="showIcon" class="w-14"></th>
        <th v-if="showName" class="h-14 text-left">
          <button class="flex items-center font-semibold focus:outline-none" @click="sortDrive" id="name">
            {{ $t("Documents.Table.Name") }}
            <span class="ml-2" v-if="orderByProperty === 'name'" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showType" class="w-30 text-left cursor-default hidden md:table-cell">
          <button class="flex items-center font-semibold focus:outline-none" @click="sortDrive" id="kind">
            {{ $t("Documents.Table.Kind") }}
            <span class="ml-2" v-if="orderByProperty === 'kind'" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showSize" class="w-25 xl:w-36 pr-6 text-right cursor-default hidden md:table-cell">
          <button class="flex items-center font-semibold focus:outline-none" @click="sortDrive" id="size">
            {{ $t("Documents.Table.Size") }}
            <span class="ml-2" v-if="orderByProperty === 'size'" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showCreatedDate" class="w-25 xl:w-40 text-right cursor-default hidden sm:table-cell">
          <button class="flex items-center font-semibold focus:outline-none" @click="sortDrive" id="createddate">
            {{ $t("Documents.Table.CreatedDate") }}
            <span class="ml-2" v-if="orderByProperty === 'createddate'" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showModifiedDate" class="w-25 xl:w-40 text-right cursor-default hidden sm:table-cell">
          <button class="flex items-center font-semibold focus:outline-none" @click="sortDrive" id="modifieddate">
            {{ $t("Documents.Table.ModifiedDate") }}
            <span class="ml-2" v-if="orderByProperty === 'modifieddate'" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showModifiedBy" class="w-1/4 text-left cursor-default pl-4 hidden md:table-cell">
          <button class="flex items-center font-semibold focus:outline-none" @click="sortDrive" id="modifiedby">
            {{ $t("Documents.Table.ModifiedBy") }}
            <span class="ml-2" v-if="orderByProperty === 'modifiedby'" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showSharedWith" class="w-1/4 text-left cursor-default pl-4 hidden md:table-cell">
          {{ $t("Documents.Table.SharedWith") }}
        </th>
        <th v-if="showSharedBy" class="w-1/4 text-left cursor-default pl-4 hidden md:table-cell">
          {{ $t("Documents.Table.SharedBy") }}
        </th>
        <th v-if="showPath" class="text-left">{{ $t("Documents.Table.Location") }}</th>
      </tr>
    </thead>

    <tbody class="relative" v-if="driveItems && isSelectedDriveItemsReady">
      <tr
        v-for="(item, index) in sortedDriveItems"
        :key="item.id"
        class="border-b border-gray-200 hover:bg-gray-150"
        :class="{ 'text-gray-400': item.nctMarkup && item.isUnderStated }"
        style="user-select: none"
        :data-name="item.name"
        :data-id="item.id"
        :data-selected="item.selected"
        @click.exact="select(item, index)"
        @click.ctrl.stop.prevent="select(item, index, 'ctrl')"
        @click.shift.stop.prevent="select(item, index, 'shift')"
        v-hover
        @hover="onDriveItemHover(item)"
      >
        <td v-if="isSelectable" class="relative">
          <div class="flex items-center h-full absolute top-0 pr-2" js-table-checkbox-wrapper>
            <span
              v-if="isClickable"
              class="flex items-center justify-center w-6 h-6 rounded-full bg-white border border-solid border-gray-200"
              js-table-checkbox
            >
              <span class="w-3 h-3 bg-primary-core rounded-full opacity-0"></span>
            </span>
          </div>
        </td>
        <td v-if="showIcon" class="py-2 w-14">
          <DocumentAvatar
            :extensionObject="item.extension"
            class="w-10 h-10 mr-4"
            :sizeClass="'text-2xl'"
          ></DocumentAvatar>
        </td>
        <td v-if="showName">
          <div class="font-semibold opacity-70 inline-block" :title="item.name">
            <span v-if="!item.extension || item.extension.name !== 'Folder'">
              <span v-if="!isClickable">
                {{ item.name }}
              </span>
              <a
                v-else-if="item.webUrl"
                :href="item.webUrl"
                target="_blank"
                class="hover:underline"
                @click="$event.stopPropagation()"
              >
                {{ item.name }}
              </a>
              <a v-else class="hover:underline cursor-pointer" @click.stop="onDriveItemClick(item)">
                {{ item.name }}
              </a>
            </span>

            <a v-else class="hover:underline cursor-pointer" @click.stop.prevent="navigateToFolder(item)">
              {{ item.name }}
            </a>
          </div>
          <br class="sm:hidden" />
          <div v-if="showCreatedDate" class="sm:hidden text-10 inline-block mr-4">
            {{ $d(item.createdDateTime, "date-long") }}
          </div>
          <div v-if="showModifiedDate" class="sm:hidden text-10 inline-block mr-4">
            {{ $d(item.showModifiedDate, "date-long") }}
          </div>
          <div v-if="showSize" class="sm:hidden text-10 inline-block mr-4">
            {{ item.sizeAsString }}
          </div>

          <slot name="document-table-name" :item="item"></slot>
        </td>
        <td v-if="showType" class="cursor-default opacity-80 hidden md:table-cell">
          <span v-if="item.extension">{{ item.extension.name }}</span>
        </td>
        <td v-if="showSize" class="cursor-default pr-6 xl:pr-14 opacity-80 hidden md:table-cell">
          <span>{{ item.sizeAsString }}</span>
        </td>
        <td v-if="showCreatedDate" class="cursor-default opacity-80 hidden sm:table-cell">
          <span class="hidden xl:inline-block">{{ $d(item.createdDateTime, "date-long") }}</span>
          <span class="xl:hidden">{{ $d(item.createdDateTime, "date-short") }}</span>
        </td>
        <td v-if="showModifiedDate" class="cursor-default opacity-80 hidden sm:table-cell">
          <span class="hidden xl:inline-block">{{ $d(item.showModifiedDate, "date-long") }}</span>
          <span class="xl:hidden">{{ $d(item.showModifiedDate, "date-short") }}</span>
        </td>
        <td v-if="showModifiedBy" class="cursor-default pl-4 hidden md:table-cell">
          {{ item && item.lastModifiedBy && item.lastModifiedBy.user ? item.lastModifiedBy.user.displayName : "" }}
        </td>
        <td v-if="showSharedWith || showSharedBy" class="cursor-default pl-4 hidden md:table-cell">
          <slot name="document-table-shared" :item="item"></slot>
        </td>
        <td v-if="showPath">
          <slot name="document-table-path" :item="item"></slot>
        </td>
      </tr>
    </tbody>
    <DocumentTableGhosts
      v-else
      :options="{
        data: driveItems
      }"
    />
  </table>
</template>

<script>
import DocumentTableGhosts from "@/components/Documents/Table/DocumentTableGhosts";
import DocumentAvatar from "@/components/Documents/DocumentAvatar";
import { sortDriveItems } from "@/helpers/driveItemHelper";
import Hover from "@/directives/Hover";
import Document from "@/models/Documents/Document";

export default {
  name: "DocumentTable",

  props: {
    driveItems: { type: Array },
    selectedItems: {
      type: Array,
      default: () => []
    },

    showIcon: { type: Boolean, default: false },
    showName: { type: Boolean, default: false },
    showType: { type: Boolean, default: false },
    showSize: { type: Boolean, default: false },
    showModifiedDate: { type: Boolean, default: false },
    showCreatedDate: { type: Boolean, default: false },
    showModifiedBy: { type: Boolean, default: false },
    showSharedWith: { type: Boolean, default: false },
    showSharedBy: { type: Boolean, default: false },
    showPath: { type: Boolean, default: false },
    isClickable: { type: Boolean, default: true },
    isSelectable: { type: Boolean, default: true }
  },

  data() {
    return {
      orderByProperty: "name",
      orderByDirection: "asc",
      isSelectedDriveItemsReady: true,
      parentOnlyHasListPermissions: true
    };
  },

  watch: {
    async driveItems(val) {
      this.isSelectedDriveItemsReady = false;
      if (val) {
        const selectedIds = this.selectedItems.map(itm => itm.id);
        const revisedSelectedItems = val.filter(itm => selectedIds.indexOf(itm.id) > -1);
        this.$emit("selectedItemsChanged", revisedSelectedItems);
      }
      this.isSelectedDriveItemsReady = true;
    }
  },

  computed: {
    sortedDriveItems() {
      const sortedList = this.driveItems.map(i => new Document(i));

      sortedList.forEach(dc => {
        if (this.selectedItems.some(sd => sd.id === dc.id)) {
          dc.selected = true;
        }
      });

      return sortDriveItems(sortedList, this.orderByProperty, this.orderByDirection);
    }
  },

  methods: {
    select(document, index, modifier) {
      /**
       * DO NOT set document.selected = true or document.selected = false directly.
       * Instead add the document to this.selectedItems and let the computed function set the property
       */

      if (!this.isSelectable) {
        return;
      }

      let selectedDriveItems = this.selectedItems;
      if (import.meta.env.VITE_BUILD_TARGET === "nordicworkplace") {
        if (document.selected) {
          selectedDriveItems.splice(selectedDriveItems.indexOf(document), 1);
        } else {
          selectedDriveItems.push(document);
        }
      } else {
        if (document.selected) {
          // Document is already selected
          if (selectedDriveItems.length > 1) {
            // There are more than this document selected
            if (modifier === "ctrl") {
              // We did press control - remove the current one
              selectedDriveItems.splice(selectedDriveItems.indexOf(document), 1);
            } else {
              // We did not press control - clear list and only select the current one
              selectedDriveItems = [];
              selectedDriveItems.push(document);
            }
          } else {
            // There are no selected documents - deselect this one too
            selectedDriveItems = [];
          }
        } else {
          // If we did not press shift or ctrl then emty the selections
          if (modifier !== "ctrl" && modifier !== "shift") {
            selectedDriveItems = [];
          }

          // If shift is pressed during click - and we already has a selected item in them list then choose all between the last selected and this one
          if (modifier === "shift" && selectedDriveItems.length > 0) {
            if (index > this.lastSelectedIndex) {
              selectedDriveItems.push(...this.sortedDriveItems.slice(this.lastSelectedIndex + 1, index));
            } else {
              selectedDriveItems.push(...this.sortedDriveItems.slice(index + 1, this.lastSelectedIndex));
            }
          }
          // And finally select this clicked element
          selectedDriveItems.push(document);
        }
      }

      this.lastSelectedIndex = index;
      this.$emit("selectedItemsChanged", selectedDriveItems);
    },

    navigateToFolder(folder) {
      if (folder.remoteItem) {
        this.$emit("folderClicked", {
          driveId: folder.remoteItem.parentReference.driveId,
          itemId: folder.remoteItem.id
        });
      } else {
        this.$emit("folderClicked", { driveId: folder.parentReference.driveId, itemId: folder.id });
      }
    },

    sortDrive(evnt) {
      if (this.orderByProperty === evnt.target.id) {
        this.orderByDirection = this.orderByDirection === "asc" ? "desc" : "asc";
      } else {
        this.orderByProperty = evnt.target.id;
      }

      this.lastSelectedIndex = 0;
    },

    onDriveItemHover(driveItem) {
      this.$emit("driveItemHover", driveItem);
    },

    onDriveItemClick(driveItem) {
      this.$emit("driveItemClick", driveItem);
    }
  },

  components: {
    DocumentTableGhosts,
    DocumentAvatar
  },

  directives: {
    Hover
  }
};
</script>

<style scoped>
[css-arrow] {
  width: 0;
  height: 0;
  position: relative;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 5px solid transparent;
  transition: transform 0.3s ease-in-out;
}

[css-arrow="asc"] {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 5px solid rgba(0, 0, 0, 0.6);
}

[css-arrow="desc"] {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 5px solid rgba(0, 0, 0, 0.6);
  transform: rotate(180deg);
}

[css-arrow="none"] {
  top: 0.0625rem;
  border-top: 0px solid rgba(0, 0, 0, 0.6);
  border-right: 5px solid rgba(0, 0, 0, 0.6);
  border-bottom: 1px solid rgba(0, 0, 0, 0.6);
  border-left: 5px solid rgba(0, 0, 0, 0.6);
}
</style>
